import HttpClient from "./http.client";
import { apiConfig } from "@/config/api.config";
import type {
  HotelRoomsResponse,
  RoomDetailsResponse,
} from "@/types/api.types";

// Initialize HTTP client for Rooms service
const roomsClient = new HttpClient(apiConfig.rooms.baseUrl);

// Room interfaces
export interface Room {
  id: string;
  hotelId: string;
  name: string;
  description: string;
  type: string;
  price: number;
  currency: string;
  capacity: number;
  bedType: string;
  size: number;
  amenities: string[];
  images: string[];
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Hotel {
  id: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  rating: number;
  amenities: string[];
  images: string[];
  phoneNumber: string;
  email: string;
  checkInTime: string;
  checkOutTime: string;
  createdAt: string;
  updatedAt: string;
}

export interface SearchFilters {
  city?: string;
  state?: string;
  checkIn?: string;
  checkOut?: string;
  guests?: number;
  minPrice?: number;
  maxPrice?: number;
  amenities?: string[];
  rating?: number;
  roomType?: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

// Rooms Service API calls
export const roomsService = {
  // Hotels
  getAllHotels: async (params?: PaginationParams) => {
    return await roomsClient.get<{ hotels: Hotel[]; total: number }>("hotels", {
      params,
    });
  },

  searchHotels: async (filters: SearchFilters) => {
    return await roomsClient.post<{ hotels: Hotel[]; total: number }>(
      "hotels/search",
      filters
    );
  },

  getHotelById: async (hotelId: string) => {
    return await roomsClient.get<Hotel>(`hotels/${hotelId}`);
  },

  getHotelsByCity: async (city: string, params?: PaginationParams) => {
    return await roomsClient.get<{ hotels: Hotel[]; total: number }>(
      `hotels/city/${city}`,
      { params }
    );
  },

  getFeaturedHotels: async (limit?: number) => {
    return await roomsClient.get<Hotel[]>("hotels/featured", {
      params: { limit },
    });
  },

  // Rooms
  getRoomsByHotel: async (hotelId: string, params?: PaginationParams) => {
    return await roomsClient.get<{ rooms: Room[]; total: number }>(
      `hotels/${hotelId}/rooms`,
      { params }
    );
  },

  // Get hotel rooms for Hotel type (propertyType=1)
  getHotelRooms: async (hotelId: string) => {
    return await roomsClient.get<HotelRoomsResponse>(
      `Room/v1/get-rooms/${hotelId}`
    );
  },

  // Get room details by room ID
  getRoomDetails: async (roomId: string) => {
    return await roomsClient.get<RoomDetailsResponse>(
      `Room/v1/get-room-details/${roomId}`
    );
  },

  getRoomById: async (roomId: string) => {
    return await roomsClient.get<Room>(`rooms/${roomId}`);
  },

  getAvailableRooms: async (
    hotelId: string,
    checkIn: string,
    checkOut: string
  ) => {
    return await roomsClient.get<Room[]>(`hotels/${hotelId}/rooms/available`, {
      params: { checkIn, checkOut },
    });
  },

  searchRooms: async (filters: SearchFilters) => {
    return await roomsClient.post<{ rooms: Room[]; total: number }>(
      "rooms/search",
      filters
    );
  },

  // Room amenities
  getRoomAmenities: async () => {
    return await roomsClient.get<string[]>("rooms/amenities");
  },

  // Room types
  getRoomTypes: async () => {
    return await roomsClient.get<string[]>("rooms/types");
  },

  // Reviews
  getHotelReviews: async (hotelId: string, params?: PaginationParams) => {
    return await roomsClient.get(`hotels/${hotelId}/reviews`, { params });
  },

  getRoomReviews: async (roomId: string, params?: PaginationParams) => {
    return await roomsClient.get(`rooms/${roomId}/reviews`, { params });
  },
};

export default roomsService;
